import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { friendlyDeviceTypeLabel } from '../devices.js';
import { useConfirm } from './ConfirmDialog.jsx';

// Account-level TankSensei device manager. Mirrors HydrosAccountPanel's
// controllers list: every claimed TankSensei device, with a per-row
// tank dropdown + unlink. Since Apex bridges through the device
// firmware (relayed onto the as/v1/.../telemetry topic), "pick which
// tank my Apex feeds" = "pick which tank my device feeds." One surface
// answers both questions instead of forcing users to walk every tank's
// Equipment panel.

export function DevicesAccountPanel() {
  const confirm = useConfirm();
  const [state, setState] = useState({ devices: null, tanks: null });
  const [pending, setPending] = useState({}); // deviceId → pick
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');

  const reload = async () => {
    try {
      const [d, t] = await Promise.all([api('/api/devices/mine'), api('/api/tanks')]);
      setState({
        devices: Array.isArray(d?.devices) ? d.devices : [],
        tanks: Array.isArray(t) ? t : [],
      });
    } catch (e) {
      setErr(e.message || 'Could not load your devices.');
      setState({ devices: [], tanks: [] });
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const moveTo = async (device, targetTankId) => {
    setErr('');
    setBusy(device.id);
    try {
      await api(`/api/devices/mine/${device.id}`, {
        method: 'PATCH',
        body: { tank_id: targetTankId || null },
      });
      setPending((p) => ({ ...p, [device.id]: '' }));
      await reload();
    } catch (e) {
      setErr(e.payload?.detail || e.message || 'Could not move the device.');
    } finally {
      setBusy(null);
    }
  };

  const unlink = async (device) => {
    const ok = await confirm({
      title: `Unlink ${device.display_name || device.client_id}?`,
      body: 'The device returns to "unclaimed." Telemetry keeps arriving but isn\'t routed to any tank until you re-claim it.',
      confirmLabel: 'Unlink',
      destructive: true,
    });
    if (!ok) return;
    setErr('');
    setBusy(device.id);
    try {
      await api(`/api/devices/mine/${device.id}`, { method: 'DELETE' });
      await reload();
    } catch (e) {
      setErr(e.message || 'Could not unlink.');
    } finally {
      setBusy(null);
    }
  };

  if (state.devices === null) {
    return (
      <div className="ts-add-form" style={{ marginTop: 12 }}>
        <p className="ts-hint">Checking your devices…</p>
      </div>
    );
  }
  if (state.devices.length === 0) return null;

  return (
    <div className="ts-add-form" style={{ marginTop: 12 }}>
      <div className="ts-add-head">
        <h4 className="font-heading">TankSensei devices</h4>
        <span className="ts-hint">Apex bridge</span>
      </div>
      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}
      <p className="ts-hint" style={{ marginTop: 6 }}>
        Pick which tank each device feeds. Apex telemetry bridges through the device, so the same
        choice routes Apex data too. Add a new device from any tank's Equipment panel.
      </p>
      <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
        {state.devices.map((d) => {
          const currentTank = state.tanks.find((t) => t.id === d.tank_id);
          const pick = pending[d.id] ?? d.tank_id ?? '';
          const dirty = pick !== (d.tank_id || '');
          return (
            <li
              key={d.id}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
                padding: '8px 0',
                borderTop: '1px solid var(--surface-border)',
              }}
            >
              <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>
                  {d.display_name || friendlyDeviceTypeLabel(d.device_type, 'TankSensei device')}
                </div>
                <div className="ts-hint">
                  {d.client_id}
                  {currentTank && ` · on ${currentTank.name}`}
                </div>
              </div>
              <select
                value={pick}
                onChange={(e) => setPending((p) => ({ ...p, [d.id]: e.target.value }))}
                style={{ minWidth: 160, flex: '1 1 160px', minHeight: 44 }}
              >
                <option value="">Not linked</option>
                {state.tanks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              {dirty && (
                <button
                  type="button"
                  className="ts-save"
                  onClick={() => moveTo(d, pick || null)}
                  disabled={busy === d.id}
                >
                  {busy === d.id ? '…' : pick ? 'Move' : 'Detach'}
                </button>
              )}
              {!dirty && (
                <button
                  type="button"
                  className="ts-chip ts-chip-danger"
                  onClick={() => unlink(d)}
                  disabled={busy === d.id}
                >
                  {busy === d.id ? '…' : 'Unlink'}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
